import { useRef, useState } from "react";
import { api, apiError } from "../lib/api";
import { openReport } from "../lib/download";
import { Button } from "./ui/button";
import { StudyMessages } from "./StudyMessages";
import { PatientDetails } from "./PatientDetails";
import { EditStudyDialog } from "./EditStudyDialog";
import { DeleteStudyButton } from "./DeleteStudyButton";
import { fmtDateLong } from "../lib/dates";
import { FileText, Upload, Trash2, Download, Loader2, Paperclip, ChevronDown, MessageSquare } from "lucide-react";
import { toast } from "sonner";

export function CompletedReportRow({ b, onReload }) {
  const [open, setOpen] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);
  const msgCount = (b.messages || []).length;
  const unread = b.unread || 0;

  const toggle = async () => {
    const next = !open;
    setOpen(next);
    if (next && unread > 0) { try { await api.patch(`/bookings/${b.id}/read`); onReload(); } catch (e) { /* noop */ } }
  };

  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.name.toLowerCase().endsWith(".pdf")) { toast.error("El informe debe ser un PDF"); return; }
    setUploading(true);
    const fd = new FormData();
    fd.append("file", file);
    try { await api.post(`/bookings/${b.id}/report`, fd); toast.success("Informe subido"); onReload(); }
    catch (err) { toast.error(apiError(err)); } finally { setUploading(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  const removeReport = async () => {
    try { await api.delete(`/bookings/${b.id}/report`); toast.success("Informe eliminado"); onReload(); }
    catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div className="border border-slate-200 rounded-xl overflow-hidden bg-white" data-testid={`completed-row-${b.id}`}>
      {/* Basic row (clickable) */}
      <button onClick={toggle} className="w-full text-left p-4 hover:bg-slate-50 transition-colors" data-testid={`completed-toggle-${b.id}`}>
        <div className="flex items-center gap-3">
          <div className="flex-1 min-w-0">
            <div className="flex flex-wrap items-center gap-2">
              {b.case_number && <span className="text-xs font-extrabold text-white bg-[#373E45] rounded-md px-2 py-0.5 tracking-widest" data-testid={`case-number-${b.id}`}>{b.case_number}</span>}
              <span className="font-bold text-[#373E45] capitalize">{fmtDateLong(b.slot_date)}</span>
              <span className="text-sm text-[#64748B]">{b.slot_start}–{b.slot_end}</span>
              {b.report_path && <Paperclip className="h-3.5 w-3.5 text-[#7FD498]" />}
              {msgCount > 0 && <span className="inline-flex items-center gap-1 text-xs font-bold text-[#5a8a68]"><MessageSquare className="h-3.5 w-3.5" />{msgCount}</span>}
              {unread > 0 && <span className="inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-red-500 text-white text-[10px] font-extrabold" data-testid={`unread-${b.id}`}>{unread}</span>}
            </div>
            <p className="text-sm text-[#373E45] font-semibold mt-1 truncate">{b.patient_name} · {b.clinic_name}</p>
          </div>
          <ChevronDown className={`h-5 w-5 text-slate-400 shrink-0 transition-transform ${open ? "rotate-180" : ""}`} />
        </div>
      </button>

      {/* Expanded detail */}
      {open && (
        <div className="px-4 pb-4 border-t border-slate-100" data-testid={`completed-detail-${b.id}`}>
          <p className="text-sm text-[#373E45] font-semibold mt-3">{b.clinic_name} · {b.contact_name}</p>
          <PatientDetails b={b} />
          <div className="mt-2"><EditStudyDialog b={b} onSaved={onReload} /></div>

          <div className="mt-4 pt-3 border-t border-slate-100">
            <p className="text-xs font-bold text-[#64748B] uppercase mb-2 flex items-center gap-1"><FileText className="h-3.5 w-3.5" />Informe de la ecografía (PDF)</p>
            {b.report_path ? (
              <div className="flex flex-wrap items-center gap-2">
                <span className="inline-flex items-center gap-1.5 text-sm font-semibold text-[#373E45] bg-[#7FD498]/15 rounded-lg px-3 py-1.5"><Paperclip className="h-4 w-4" />{b.report_filename}</span>
                <Button size="sm" variant="outline" onClick={() => openReport(b.id)} className="rounded-xl border-slate-300 font-semibold text-[#373E45]" data-testid={`download-report-${b.id}`}><Download className="h-4 w-4 mr-1" />Ver / descargar</Button>
                <Button size="sm" variant="outline" onClick={removeReport} className="rounded-xl border-slate-300 text-red-600 hover:text-red-700" data-testid={`delete-report-${b.id}`}><Trash2 className="h-4 w-4 mr-1" />Eliminar</Button>
              </div>
            ) : (
              <div>
                <input ref={fileRef} type="file" accept="application/pdf" onChange={upload} className="hidden" data-testid={`report-file-${b.id}`} />
                <Button size="sm" onClick={() => fileRef.current?.click()} disabled={uploading} className="bg-[#7FD498] hover:bg-[#6BC284] text-[#373E45] font-bold rounded-xl" data-testid={`upload-report-${b.id}`}>
                  {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Upload className="h-4 w-4 mr-1" />Subir informe PDF</>}
                </Button>
              </div>
            )}
          </div>

          <StudyMessages b={b} onReload={onReload} meRole="admin" />

          <div className="mt-3 pt-2 border-t border-slate-100 flex justify-end">
            <DeleteStudyButton b={b} onDeleted={onReload} />
          </div>
        </div>
      )}
    </div>
  );
}
